/**
 * Words and links for the trade page, kept pure so they are tested without a
 * browser. Registry facts arrive as codes (NAF activity, SIRENE headcount
 * band, reason keys); these turn them into what a vendor reads.
 */
import type { TradeStatus } from '../../../common/hooks/use-trade-api.hook';
import type { StatusTone } from '../../../components/design-system/admin/StatusBadge';

/** i18next's `t`, loosely: its overloads do not fit a narrower signature. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type T = (...args: any[]) => any;

/** Employees by SIRENE band, as a range. */
const BANDS: Record<string, [number, number | null]> = {
    '00': [0, 0], '01': [1, 2], '02': [3, 5], '03': [6, 9], '11': [10, 19], '12': [20, 49], '21': [50, 99], '22': [100, 199],
    '31': [200, 249], '32': [250, 499], '41': [500, 999], '42': [1000, 1999], '51': [2000, 4999], '52': [5000, 9999], '53': [10000, null],
};

export function headcountLabel(band: string | null | undefined, t: T, locale = 'en'): string {
    const range = band ? BANDS[band] : undefined;
    if (!range || band === '00') return String(t('TRADE.SIZE.NONE', 'No employees declared'));
    const n = (v: number) => new Intl.NumberFormat(locale).format(v);
    return String(range[1] === null
        ? t('TRADE.SIZE.OVER', '{{min}}+ employees', { min: n(range[0]) })
        : t('TRADE.SIZE.RANGE', '{{min}}–{{max}} employees', { min: n(range[0]), max: n(range[1]) }));
}

/** The short form for a table cell: "10–19", "—". */
export function headcountShort(band: string | null | undefined): string {
    const range = band ? BANDS[band] : undefined;
    if (!range || band === '00') return '—';
    return range[1] === null ? `${range[0]}+` : `${range[0]}–${range[1]}`;
}

/** NAF code → the trade, in the vendor's language ("43.33Z" → TRADE.ACTIVITY.43_33Z). */
export function activityKey(code: string): string {
    return `TRADE.ACTIVITY.${String(code).replace('.', '_')}`;
}

export function activityLabel(code: string, t: T, fallback?: string): string {
    return String(t(activityKey(code), fallback || code));
}

export function reasonLabel(reason: string, t: T): string {
    return String(t(`TRADE.REASON.${reason}`, reason.toLowerCase().replace(/_/g, ' ')));
}

export function leadReasonLabel(reason: string, t: T): string {
    return String(t(`TRADE.LEAD_REASON.${reason}`, reason.toLowerCase().replace(/_/g, ' ')));
}

export const STATUS_TONE: Record<TradeStatus, StatusTone> = {
    new: 'neutral',
    contacted: 'info',
    meeting: 'warning',
    customer: 'success',
    ignored: 'neutral',
};

export function formatKm(km: number | null | undefined, locale = 'en'): string {
    if (typeof km !== 'number' || !Number.isFinite(km)) return '—';
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: km < 10 ? 1 : 0 }).format(km)} km`;
}

/** The year the business started, or a dash. */
export function sinceYear(createdOn: string | null | undefined): string {
    if (!createdOn) return '—';
    const d = new Date(createdOn);
    return Number.isFinite(d.getTime()) ? String(d.getUTCFullYear()) : '—';
}

/** A mail draft the vendor's own mail app opens: no recipient (the registry has none), subject and body filled. */
export function mailtoHref(subject: string, body: string, to = ''): string {
    const enc = (s: string) => encodeURIComponent(s);
    return `mailto:${to}?subject=${enc(subject)}&body=${enc(body.replace(/\r?\n/g, '\r\n'))}`;
}

/** Where to find their phone number or email: the registry has neither. */
export function webSearchHref(name: string, city?: string | null): string {
    return `https://www.google.com/search?q=${encodeURIComponent([name, city].filter(Boolean).join(' '))}`;
}

/** Map pin colour by status — colour only where it means something. A theme token name. */
export type PinTone = 'foreground' | 'muted' | 'success' | 'warning';
export function statusPinTone(status: TradeStatus): PinTone {
    switch (status) {
        case 'customer': return 'success';
        case 'meeting': return 'warning';
        case 'contacted': return 'foreground';
        default: return 'muted';
    }
}

/** True when the last check of the leads is missing or older than a day. */
export function leadsCheckStale(lastCheckAt: string | null | undefined, now = Date.now()): boolean {
    if (!lastCheckAt) return true;
    const t = new Date(lastCheckAt).getTime();
    return !Number.isFinite(t) || now - t > 24 * 3600 * 1000;
}
